import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { TablePagination } from "@mui/material";
import {
  Agenda,
  Day,
  DragAndDrop,
  Inject,
  Month,
  MonthAgenda,
  PopupOpenEventArgs,
  Resize,
  ResourceDirective,
  ResourcesDirective,
  ScheduleComponent,
  TimelineMonth,
  TimelineViews,
  ViewDirective,
  ViewsDirective,
  Week,
  WorkWeek,
} from "@syncfusion/ej2-react-schedule";
import { useTranslate } from "@tolgee/react";
import { sortBy, uniqBy } from "lodash-es";
import moment from "moment";
import React from "react";
import { useNavigate } from "react-router-dom";
import { ModifyPlanSessionInput } from "../../../clients/graphqlTypes.js";
import { Page } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { usePaginationParamsInUrl } from "../../commons/pagination/usePaginationParams.js";
import { actionBegin } from "../../features/schedule/helpers.js";
import {
  ScheduleQuery,
  ScheduleResourcesQuery,
  useScheduleModifyPlanSessionMutation,
  useScheduleQuery,
  useScheduleResourcesQuery,
} from "../../features/schedule/Schedule.generated.js";

export const popupOpen =
  (navigate: ReturnType<typeof useNavigate>) => (args?: PopupOpenEventArgs) => {
    if (!args) return;

    args.cancel = true;

    if (args.type === "QuickInfo") {
      // noop
    } else if (args.type === "Editor") {
      const data: any = args.data;

      if (data && data.ProjectId) {
        setTimeout(() => {
          // let ScheduleComponent do its stuff and new push route on next tick
          navigate(`/projects/${data.ProjectId}/overview`);
        }, 0);
      }
    }
  };

function extractProjects(source: ScheduleQuery) {
  return (
    getDataOrNull(source.projectsAsContractor)?.edges.map(e => e.node) ?? []
  );
}

function extractData(source: ReturnType<typeof extractProjects>) {
  return source
    .map(p => {
      return p.planSessions.map(ps => {
        return {
          Id: ps.id,
          Subject: p.title,
          IsAllDay: false,
          ProjectId: p.id,
          WhoId: ps.who.id,

          StartTime: moment(ps.from).local().toDate(),
          EndTime: moment(ps.till).local().toDate(),
        };
      });
    })
    .flat(1);
}

function extractWhoData(source: ScheduleResourcesQuery) {
  return sortBy(
    uniqBy(
      source.organisationMemberships.edges
        .map(edge => {
          return {
            WhoTitle: `${edge.node.firstname} ${edge.node.familyname}`,
            Id: edge.node.id,
            WhoColor: "#cb6bb2",
          };
        })
        .flat(1),
      "Id"
    ),
    "WhoTitle"
  );
}

export const Schedule = () => {
  const client = useApolloClient();
  const query = useScheduleQuery({
    client,
    fetchPolicy: "cache-and-network",
  });
  const data = query.data;

  const [modifyPlanSession] = useScheduleModifyPlanSessionMutation({
    client,
  });

  if (!data) return null;

  return (
    <ScheduleInternal
      queryData={data}
      modifyPlanSession={planSession =>
        modifyPlanSession({ variables: { planSession } })
      }
    />
  );
};

const ScheduleInternal = React.memo(
  ({
    queryData,
    modifyPlanSession,
  }: {
    queryData: ScheduleQuery;
    modifyPlanSession: (
      planSession: ModifyPlanSessionInput
    ) => Promise<unknown>;
  }) => {
    const { t } = useTranslate(["Global"]);

    const navigate = useNavigate();

    const projects = extractProjects(queryData);
    const data = extractData(projects);

    const { paginationModel, setPaginationModel } = usePaginationParamsInUrl({
      perPageDefaultAmount: 100,
      pageUrlParam: "resourcesPage",
      perPageUrlParam: "resourcesPerPage",
    });

    const client = useApolloClient();
    const resourcesQuery = useScheduleResourcesQuery({
      client,
      fetchPolicy: "network-only",
      variables: {
        limit: paginationModel.pageSize,
        offset: paginationModel.pageSize * paginationModel.page,
      },
    });

    const whoData = resourcesQuery?.data
      ? extractWhoData(resourcesQuery?.data)
      : [];
    const totalCount =
      resourcesQuery.data?.organisationMemberships.totalCount ?? 0;

    return (
      <Page>
        <PageContainer>
          <ScheduleComponent
            popupOpen={popupOpen(navigate)}
            actionBegin={actionBegin(async (data: any) => {
              const { Id, StartTime, EndTime, WhoId, IsTentative } = data;

              const from = moment(StartTime),
                till = moment(EndTime);

              try {
                await modifyPlanSession({
                  id: Id,
                  from: from.toISOString(true),
                  till: till.toISOString(true),
                  whoId: WhoId,
                  isTentative: IsTentative,
                });
              } catch (err) {
                console.error(err);
                alert("Something went wrong; reload page");
              }
            })}
            group={{
              resources: ["Whos"],
            }}
            rowAutoHeight
            height="100%"
            currentView="TimelineMonth"
            width="100%"
            eventSettings={{
              allowDeleting: false,
              dataSource: data,
              fields: {
                id: "Id",
                subject: { name: "Subject" },
                isAllDay: { name: "IsAllDay" },
              },
            }}
          >
            <ResourcesDirective>
              <ResourceDirective
                field="WhoId"
                title="Who"
                name="Whos"
                allowMultiple={true}
                dataSource={whoData}
                textField="WhoTitle"
                idField="Id"
                colorField="WhoColor"
              />
            </ResourcesDirective>

            <ViewsDirective>
              <ViewDirective option="TimelineDay" allowVirtualScrolling />
              <ViewDirective
                firstDayOfWeek={1}
                option="TimelineWeek"
                timeScale={{ interval: 60 * 24, enable: true, slotCount: 1 }}
                allowVirtualScrolling
              />
              <ViewDirective option="TimelineMonth" allowVirtualScrolling />
              {/*<ViewDirective option="Agenda" />*/}
              {/*<ViewDirective option="MonthAgenda" firstDayOfWeek={1} />*/}
            </ViewsDirective>

            <Inject
              services={[
                Day,
                Week,
                WorkWeek,
                Month,
                Agenda,
                MonthAgenda,
                TimelineViews,
                TimelineMonth,
                DragAndDrop,
                Resize,
              ]}
            />
          </ScheduleComponent>
          <TablePagination
            component="div"
            count={totalCount}
            page={paginationModel.page}
            onPageChange={(event, newPage) => {
              setPaginationModel({
                pageSize: paginationModel.pageSize,
                page: newPage,
              });
            }}
            rowsPerPageOptions={[50, 100, 250]}
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={event => {
              setPaginationModel({
                pageSize: parseInt(event.target.value, 10),
                page: 0,
              });
            }}
            labelRowsPerPage={t("Per page:", { ns: "Global" })}
          />
        </PageContainer>
      </Page>
    );
  },
  () => {
    // TODO for some reason the syncfusion schedule component is not playing nice with re-rendering the <ScheduleComponent>, for now, make it behave are totally memoized
    return true;
  }
);
